import React, { useState, useEffect } from "react";
import { Container, Typography, Box, Card, CardContent, TextField, Button, LinearProgress, Grid, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";

const Goals = () => {
  const [dailyGoal, setDailyGoal] = useState(120); // minutes
  const [weeklyGoal, setWeeklyGoal] = useState(600); // minutes
  const [editDailyGoal, setEditDailyGoal] = useState("");
  const [editWeeklyGoal, setEditWeeklyGoal] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [currentProgress, setCurrentProgress] = useState({ daily: 0, weekly: 0 });

  useEffect(() => {
    // Load goals from localStorage
    const savedDailyGoal = localStorage.getItem("dailyGoal");
    const savedWeeklyGoal = localStorage.getItem("weeklyGoal");

    if (savedDailyGoal) setDailyGoal(parseInt(savedDailyGoal));
    if (savedWeeklyGoal) setWeeklyGoal(parseInt(savedWeeklyGoal));

    calculateProgress();
  }, []);

  const calculateProgress = () => {
    const sessions = JSON.parse(localStorage.getItem("sessions")) || [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Calculate today's and this week's actual focus time from completed pomodoro sessions
    let dailyMinutes = 0;
    let weeklyMinutes = 0;

    sessions
      .filter((session) => session.type === "pomodoro")
      .forEach((session) => {
        const sessionDate = new Date(session.completedAt);
        sessionDate.setHours(0, 0, 0, 0);

        const daysAgo = Math.floor((today - sessionDate) / (1000 * 60 * 60 * 24));
        if (daysAgo >= 0 && daysAgo < 7) {
          weeklyMinutes += session.duration || 0;
          if (daysAgo === 0) {
            dailyMinutes += session.duration || 0;
          }
        }
      });

    setCurrentProgress({
      daily: dailyMinutes,
      weekly: weeklyMinutes,
    });
  };

  const handleSaveGoals = () => {
    const newDailyGoal = editDailyGoal ? parseInt(editDailyGoal) : dailyGoal;
    const newWeeklyGoal = editWeeklyGoal ? parseInt(editWeeklyGoal) : weeklyGoal;

    setDailyGoal(newDailyGoal);
    setWeeklyGoal(newWeeklyGoal);
    localStorage.setItem("dailyGoal", newDailyGoal.toString());
    localStorage.setItem("weeklyGoal", newWeeklyGoal.toString());

    setEditDailyGoal("");
    setEditWeeklyGoal("");
    setOpenDialog(false);
    calculateProgress();
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const dailyProgress = Math.min((currentProgress.daily / dailyGoal) * 100, 100);
  const weeklyProgress = Math.min((currentProgress.weekly / weeklyGoal) * 100, 100);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography
        variant="h4"
        align="center"
        sx={{
          fontWeight: 900,
          fontFamily: "'Montserrat', sans-serif",
          background: "linear-gradient(90deg, #FF9800, #F57C00)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          textTransform: "uppercase",
          letterSpacing: "2px",
          mb: 4,
        }}
        gutterBottom
      >
        Focus Time Goals
      </Typography>

      <Button
        variant="contained"
        startIcon={<SaveIcon />}
        onClick={() => setOpenDialog(true)}
        sx={{ mb: 4, background: "linear-gradient(45deg, #3F51B5, #2196F3)" }}
      >
        Edit Goals
      </Button>

      {/* Daily Goal */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📅 Daily Goal
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">
                    {formatTime(currentProgress.daily)} / {formatTime(dailyGoal)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: dailyProgress >= 100 ? "#4CAF50" : "#FF9800" }}>
                    {dailyProgress.toFixed(1)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={dailyProgress}
                  sx={{
                    height: 10,
                    borderRadius: "5px",
                    backgroundColor: "#e0e0e0",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: dailyProgress >= 100 ? "#4CAF50" : "#FF9800",
                    },
                  }}
                />
              </Box>
              {dailyProgress >= 100 ? (
                <Typography variant="body2" sx={{ color: "#4CAF50", fontWeight: "bold" }}>
                  ✅ Daily goal achieved!
                </Typography>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {formatTime(Math.max(0, dailyGoal - currentProgress.daily))} to go
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Weekly Goal */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📊 Weekly Goal
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">
                    {formatTime(currentProgress.weekly)} / {formatTime(weeklyGoal)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: weeklyProgress >= 100 ? "#4CAF50" : "#2196F3" }}>
                    {weeklyProgress.toFixed(1)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={weeklyProgress}
                  sx={{
                    height: 10,
                    borderRadius: "5px",
                    backgroundColor: "#e0e0e0",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: weeklyProgress >= 100 ? "#4CAF50" : "#2196F3",
                    },
                  }}
                />
              </Box>
              {weeklyProgress >= 100 ? (
                <Typography variant="body2" sx={{ color: "#4CAF50", fontWeight: "bold" }}>
                  ✅ Weekly goal achieved!
                </Typography>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {formatTime(Math.max(0, weeklyGoal - currentProgress.weekly))} to go
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Goals Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Edit Goals</DialogTitle>
        <DialogContent sx={{ pt: 3, minWidth: "400px" }}>
          <TextField
            label="Daily Goal (minutes)"
            type="number"
            defaultValue={dailyGoal}
            onChange={(e) => setEditDailyGoal(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Weekly Goal (minutes)"
            type="number"
            defaultValue={weeklyGoal}
            onChange={(e) => setEditWeeklyGoal(e.target.value)}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveGoals} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Goals;
